import type {
  AgentActionPlan,
  AgentSideEffect,
  AgentWidget,
  AgentWidgetAction,
  AgentWidgetType,
  PresetActionId,
  PresetOption,
} from "@/types/createAgent";
import {
  getCategoryGridActionId,
  getChoicePresets,
  getCommitPresets,
  getPresetPresentationType,
  isCreditPresetAction,
} from "@/lib/agent/presetPresentation";

export const WIDGET_VERSION = 1;

export const LEGACY_WIDGET_TYPE_ALIASES: Partial<
  Record<AgentWidgetType, AgentWidgetType>
> = {
  choice_set: "field_picker",
  color_swatches: "field_picker",
  catalog_grid: "catalog_picker",
  action_bar: "action_dock",
  upload_picker: "upload_request",
};

export function normalizeWidgetType(type: AgentWidgetType): AgentWidgetType {
  return LEGACY_WIDGET_TYPE_ALIASES[type] ?? type;
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
}

function sideEffectTypeForAction(
  actionId: PresetActionId,
  actionData: PresetOption["actionData"]
): AgentSideEffect["type"] | undefined {
  if (isCreditPresetAction(actionId)) {
    return "generate_image";
  }
  if (actionData?.shouldCreateTicket || actionId === "submit_quote") {
    return "create_ticket";
  }
  if (actionData?.shouldRequestLocation || actionId === "request_location") {
    return "request_location";
  }
  if (actionId === "start_over") {
    return "reset_flow";
  }
  if (actionId === "start_virtual_try_on") {
    return "open_virtual_try_on";
  }
  return undefined;
}

function costCreditsForAction(actionId: PresetActionId): number | undefined {
  return isCreditPresetAction(actionId) ? 1 : undefined;
}

export function widgetActionFromPreset(
  preset: PresetOption,
  index: number
): AgentWidgetAction {
  return {
    id: `${preset.actionId}-${slugify(preset.label) || index}`,
    label: preset.label,
    action: preset.action,
    actionId: preset.actionId,
    presetType: getPresetPresentationType(preset),
    data: preset.data,
    ...(preset.actionData ? { actionData: preset.actionData } : {}),
    ...(costCreditsForAction(preset.actionId)
      ? { costCredits: costCreditsForAction(preset.actionId) }
      : {}),
    ...(sideEffectTypeForAction(preset.actionId, preset.actionData)
      ? {
          sideEffectType: sideEffectTypeForAction(
            preset.actionId,
            preset.actionData
          ),
        }
      : {}),
  };
}

export function inferWidgetTypeForPresets(
  presets: PresetOption[]
): AgentWidgetType {
  if (presets.length === 0) {
    return "field_picker";
  }

  if (
    presets.some(
      preset => getPresetPresentationType(preset) === "upload_choice"
    )
  ) {
    return "upload_request";
  }

  const categoryGridActionId = getCategoryGridActionId(presets);
  if (categoryGridActionId && presets.length > 5) {
    return "catalog_picker";
  }

  if (
    presets.every(preset => getPresetPresentationType(preset) === "vibe_bundle")
  ) {
    return "concept_picker";
  }

  if (
    presets.every(
      preset => getPresetPresentationType(preset) === "commit_action"
    )
  ) {
    return "action_dock";
  }

  return "field_picker";
}

export function inferWidgetTypeForGroup(params: {
  group: AgentActionPlan["groups"][number];
  phase?: string;
  hasFrontImage?: boolean;
  hasBackImage?: boolean;
}): AgentWidgetType {
  const { group } = params;
  if (group.type === "concept_choices") return "concept_picker";
  if (group.type === "catalog_choices") return "catalog_picker";
  if (group.type === "upload_choices") return "upload_request";
  if (group.type === "commit_actions") {
    const actionIds = new Set(group.presets.map(preset => preset.actionId));
    const reviewActions = [
      "submit_quote",
      "start_virtual_try_on",
      "continue_to_back_design",
      "modify_dress_front",
      "modify_dress_back",
      "generate_dress_back",
    ];
    if (
      params.phase === "front_review" ||
      params.phase === "back_review" ||
      reviewActions.some(actionId => actionIds.has(actionId as PresetActionId))
    ) {
      return "review_action_panel";
    }
    return "action_dock";
  }
  return inferWidgetTypeForPresets(group.presets);
}

function titleForWidget(type: AgentWidgetType): string {
  switch (normalizeWidgetType(type)) {
    case "action_dock":
      return "Next actions";
    case "catalog_picker":
      return "Browse options";
    case "upload_request":
      return "Upload references";
    case "concept_picker":
      return "Choose a direction";
    case "field_picker":
      return "Suggested choices";
    case "review_action_panel":
      return "Review actions";
    default:
      return "Design update";
  }
}

function buildWidget(
  type: AgentWidgetType,
  presets: PresetOption[],
  index: number
): AgentWidget {
  return {
    id: `compat-${type}-${index}`,
    version: WIDGET_VERSION,
    type,
    active: true,
    title: titleForWidget(type),
    presets,
    actions: presets.map(widgetActionFromPreset),
  };
}

export function buildCompatibilityWidgets(
  presets: PresetOption[] | undefined
): AgentWidget[] {
  if (!presets || presets.length === 0) {
    return [];
  }

  const choicePresets = getChoicePresets(presets);
  const commitPresets = getCommitPresets(presets);
  const widgets: AgentWidget[] = [];

  if (choicePresets.length > 0) {
    widgets.push(
      buildWidget(
        inferWidgetTypeForPresets(choicePresets),
        choicePresets,
        widgets.length
      )
    );
  }

  if (commitPresets.length > 0) {
    widgets.push(buildWidget("action_dock", commitPresets, widgets.length));
  }

  return widgets;
}
